import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Copy, RotateCw, Check, ExternalLink, Lock, Loader2 } from "lucide-react";

export default function ShareDialog({ open, onOpenChange, client }) {
    const [token, setToken] = useState(client?.share_token || "");
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);
    const [protectedState, setProtectedState] = useState(false);
    const [showPasswordField, setShowPasswordField] = useState(false);
    const [password, setPassword] = useState("");
    const [savingPw, setSavingPw] = useState(false);

    const url = token ? `${window.location.origin}/portal/${token}` : "";

    useEffect(() => {
        if (!open || !client?.client_id) return;
        setToken(client.share_token || "");
        setShowPasswordField(false);
        setPassword("");
        api.get(`/clients/${client.client_id}/portal-password`)
            .then(({ data }) => setProtectedState(!!data?.protected))
            .catch(() => {});
    }, [open, client?.client_id, client?.share_token]);

    const ensureToken = async () => {
        if (token) return;
        setLoading(true);
        try {
            const { data } = await api.post(`/clients/${client.client_id}/share`);
            setToken(data.share_token);
        } finally { setLoading(false); }
    };

    const rotate = async () => {
        setLoading(true);
        try {
            const { data } = await api.post(`/clients/${client.client_id}/share/rotate`);
            setToken(data.share_token);
            toast.success("New link generated", { description: "The previous link no longer works." });
        } finally { setLoading(false); }
    };

    const copy = async () => {
        await ensureToken();
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            toast.success("Link copied to clipboard");
            setTimeout(() => setCopied(false), 2000);
        } catch { toast.error("Could not copy — please copy manually"); }
    };

    const toggleProtection = async (v) => {
        if (!v) {
            setSavingPw(true);
            try {
                await api.delete(`/clients/${client.client_id}/portal-password`);
                setProtectedState(false);
                setShowPasswordField(false);
                setPassword("");
                toast.success("Password protection removed");
            } catch (err) {
                toast.error("Could not remove password", { description: formatApiErrorDetail(err.response?.data?.detail) });
            } finally { setSavingPw(false); }
        } else {
            setShowPasswordField(true);
        }
    };

    const savePassword = async () => {
        if (password.length < 4) { toast.error("Password must be at least 4 characters"); return; }
        setSavingPw(true);
        try {
            await api.post(`/clients/${client.client_id}/portal-password`, { password });
            setProtectedState(true);
            setShowPasswordField(false);
            setPassword("");
            toast.success("Portal password saved");
        } catch (err) {
            toast.error("Could not save password", { description: formatApiErrorDetail(err.response?.data?.detail) });
        } finally { setSavingPw(false); }
    };

    if (open && !token && !loading) ensureToken();

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[540px] rounded-xl border-slate-200">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl tracking-tight">Share {client?.company_name} portal</DialogTitle>
                    <DialogDescription className="text-slate-500">
                        Anyone with this link can view the portal. They don't need a ClientSpace account.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-2 pt-2">
                    <Label className="text-slate-700">Public portal link</Label>
                    <div className="flex gap-2">
                        <Input readOnly value={url || "Generating link…"}
                               data-testid="share-url-input"
                               className="font-mono text-xs ring-terra" />
                        <button onClick={copy} data-testid="share-copy-btn"
                                className="btn-terra rounded-lg px-3 text-sm font-medium inline-flex items-center gap-1.5">
                            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                            {copied ? "Copied" : "Copy"}
                        </button>
                    </div>
                </div>

                <div className="mt-4 rounded-lg border border-slate-200 p-4">
                    <div className="flex items-center gap-3">
                        <Lock className="w-4 h-4 text-slate-500" />
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-slate-900">Password protect this portal</p>
                            <p className="text-xs text-slate-500 mt-0.5">
                                {protectedState
                                    ? <span className="text-emerald-700" data-testid="portal-protected-badge">Protected — clients must enter a password to view.</span>
                                    : "Off — anyone with the link can open the portal."}
                            </p>
                        </div>
                        <Switch
                            checked={protectedState || showPasswordField}
                            disabled={savingPw}
                            onCheckedChange={toggleProtection}
                            data-testid="portal-password-toggle"
                        />
                    </div>
                    {showPasswordField && (
                        <div className="mt-3 flex items-end gap-2" data-testid="portal-password-field">
                            <div className="flex-1 space-y-1">
                                <Label className="text-slate-700 text-xs">
                                    {protectedState ? "Change password" : "New password"}
                                </Label>
                                <Input
                                    type="password" value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    data-testid="portal-password-input"
                                    className="ring-terra" placeholder="Minimum 4 characters"
                                    autoComplete="new-password" minLength={4}
                                />
                            </div>
                            <button onClick={savePassword} disabled={savingPw}
                                    data-testid="portal-password-save"
                                    className="btn-terra rounded-lg px-3 py-2 text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-70">
                                {savingPw && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save
                            </button>
                        </div>
                    )}
                    {protectedState && !showPasswordField && (
                        <button
                            onClick={() => setShowPasswordField(true)}
                            data-testid="portal-password-change-btn"
                            className="mt-3 text-xs font-medium text-slate-600 hover:text-slate-900">
                            Change password
                        </button>
                    )}
                </div>

                <div className="flex items-center justify-between mt-6">
                    <a href={url || "#"} target="_blank" rel="noreferrer"
                       data-testid="share-open-link"
                       className="text-sm font-medium text-slate-600 hover:text-slate-900 inline-flex items-center gap-1.5">
                        <ExternalLink className="w-4 h-4" /> Open portal
                    </a>
                    <button onClick={rotate} disabled={loading}
                            data-testid="share-rotate-btn"
                            className="text-sm font-medium text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5">
                        <RotateCw className="w-3.5 h-3.5" /> Generate new link
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
